import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  Alert,
  Box,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import ComercialDrawer from "../../../../components/general/ComercialDrawer";
import WorkingScaleSelector from "../../../../components/comercial/WorkingScaleSelector";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
  DEFAULT_WORKING_TIME,
  POSSIBLE_WORKING_TIMES,
} from "../../workingTimeConstants";
import { PrimaryButton } from "../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../components/general/buttons/SecondaryButton";
import GalleryImageList from "../../../../components/comercial/ImageGallery";
import { LeftMarginBackButton } from "../../../../components/general/buttons/BackPageButton";
import fetchCep from "../../../../hooks/useCep";
import { sharedStyles } from "../../../../styles/shared";
import ImageGalleryContainer from "../../../../components/comercial/ImageGallery/ImageGalleryContainer";
import EmptyGallery from "@/components/comercial/ImageGallery/EmptyGallery";
import {
  addAndEditStoreSchema,
  validateCnpj,
  validatePhoneNumber,
} from "../agencyValidationSchemas";
import MaskedTextField from "@/components/general/MaskedTextField";
import Tiptap from "@/components/general/Tiptap";
import { CEP_MASK, CNPJ_MASK, PHONE_MASK } from "@/utils/stringUtils";
import AddImageInput from "@/components/comercial/AddImageInput";
import { handleCreateStore } from "./functions";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import CoordinateSelector from "@/components/general/CoordinateSelector";
import { handleNextStep, handlePreviousStep } from "../../sharedFunctions";
import { EXCURSION_OPERATION_STEPS } from "../../Excursions/AddExcursion/functions";
import NewOne from "@/components/comercial/WorkingScaleSelector/NewOne";

export const DEFAULT_COORDINATES: [number, number] = [-1, -1];

const AddAgency = () => {
  const navigate = useNavigate();

  const [cnpj, setCnpj] = useState("");
  const [phone, setPhone] = useState<string>("");
  const descriptionRef = useRef("");

  const [cep, setCep] = useState<string>("");

  const streetRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);

  const [coordinates, setCoordinates] =
    useState<[number, number]>(DEFAULT_COORDINATES);

  const foundedCepAddress = fetchCep(
    cep,
    streetRef,
    districtRef,
    cityRef,
    stateRef,
    true
  );

  const [images, setImages] = useState<HTMLCanvasElement[]>([]);

  const [timeMonday, setTimeMonday] = useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeTuesday, setTimeTuesday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeWednesday, setTimeWednesday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeThursday, setTimeThursday] =
    useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeFriday, setTimeFriday] = useState<string[]>(DEFAULT_WORKING_TIME);
  const [timeSaturday, setTimeSaturday] = useState<string[]>([]);
  const [timeSunday, setTimeSunday] = useState<string[]>([]);

  const [submittionObject, setSubmittionObject] = useState(null);
  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);

  const [additionLoading, setAdditionLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
    getValues,
  } = useForm({ resolver: yupResolver(addAndEditStoreSchema) });

  const [additionStep, setAdditionStep] = useState(0);

  const formValues = getValues();

  const isCnpjValidated = useMemo(() => validateCnpj(cnpj), [cnpj]);
  const isPhoneValidated = useMemo(() => validatePhoneNumber(phone), [phone]);

  const handleFirstStepSubmit = () => {
    if (!submittedOnce) setSubmittedOnce(true);

    setError(null);

    if (isCnpjValidated && isPhoneValidated) {
      setSubmittionObject({
        name: formValues.name,
        CNPJ: cnpj,
        email: formValues.email,
        phone: phone,
        description: DOMPurify.sanitize(descriptionRef.current),
      });

      setCep("");
      setCoordinates(DEFAULT_COORDINATES);
      handleNextStep(
        additionStep,
        setAdditionStep,
        EXCURSION_OPERATION_STEPS.length
      );
    }
  };

  const handleSecondStepSubmit = () => {
    setSubmittionObject({
      ...submittionObject,
      CEP: cep,
      logradouro: streetRef.current.value,
      number: numberRef.current.value,
      district: districtRef.current.value,
      city: cityRef.current.value,
      state: stateRef.current.value,
      lat: coordinates[0],
      lng: coordinates[1],
    });

    handleNextStep(
      additionStep,
      setAdditionStep,
      EXCURSION_OPERATION_STEPS.length
    );
  };

  const handleThirdStepSubmit = () => {
    setSubmittionObject({
      ...submittionObject,
      images: images,
    });

    handleNextStep(
      additionStep,
      setAdditionStep,
      EXCURSION_OPERATION_STEPS.length
    );
  };

  const handleFourthStepSubmit = () => {
    setAdditionLoading(true);

    handleCreateStore({
      ...submittionObject,
      timeMonday,
      timeTuesday,
      timeWednesday,
      timeThursday,
      timeFriday,
      timeSaturday,
      timeSunday,
    })
      .then((response) => {
        alert(`Agência ${response.name} criada com sucesso!`);
        navigate("/agencies");
      })
      .catch((error) => {
        setError(error.response.data.message);
        setAdditionStep(0);
      })
      .finally(() => setAdditionLoading(false));
  };

  const renderStoreDetails = (
    <form onSubmit={onSubmit(handleFirstStepSubmit)}>
      <Box sx={{ ...sharedStyles.verticalContent, gap: 2 }}>
        <Box sx={sharedStyles.dividedFormContainer}>
          <TextField
            size="small"
            label="Nome da agência *"
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
            fullWidth
          />

          <MaskedTextField
            label="CNPJ *"
            mask={CNPJ_MASK}
            value={cnpj}
            setValue={setCnpj}
            maskChar={"_"}
            error={!isCnpjValidated && submittedOnce}
            helperText={!isCnpjValidated && submittedOnce && "CNPJ inválido."}
          />
        </Box>

        <Box sx={sharedStyles.dividedFormContainer}>
          <TextField
            size="small"
            label="Email para contato *"
            placeholder="Ex.: contato@email.com"
            fullWidth
            {...register("email")}
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <MaskedTextField
            label="Telefone para contato *"
            mask={PHONE_MASK}
            maskChar={"_"}
            value={phone}
            setValue={setPhone}
            error={!isPhoneValidated && submittedOnce}
            helperText={
              !isPhoneValidated && submittedOnce && "Telefone inválido."
            }
          />
        </Box>

        <Tiptap
          onChange={(newText) => (descriptionRef.current = newText)}
          placeholder="Descreva brevemente sua agência..."
          defaultValue={descriptionRef.current}
        />

        {error && (
          <Alert severity="error" sx={{ marginY: 2 }}>
            {error}
          </Alert>
        )}

        <PrimaryButton
          label="Confirmar"
          styles={{ ...sharedStyles.secondaryColor, marginY: 2 }}
        />
      </Box>
    </form>
  );

  const renderGeographicData = (
    <Box sx={sharedStyles.verticalContent}>
      <MaskedTextField
        label="CEP"
        mask={CEP_MASK}
        value={cep}
        setValue={setCep}
        maskChar={"_"}
      />

      <Box sx={sharedStyles.horizontalContent}>
        <TextField
          label="Logradouro"
          defaultValue={submittionObject?.logradouro}
          inputRef={streetRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size="small"
          disabled={
            foundedCepAddress !== null && streetRef?.current?.value !== ""
          }
        />

        <TextField
          label="N°"
          defaultValue={submittionObject?.number}
          inputRef={numberRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size="small"
          sx={{ width: "35%" }}
        />
      </Box>

      <TextField
        label="Bairro"
        defaultValue={submittionObject?.district}
        inputRef={districtRef}
        InputLabelProps={{ shrink: true }}
        fullWidth
        disabled={
          foundedCepAddress !== null && districtRef?.current?.value !== ""
        }
        size="small"
      />

      <Box sx={sharedStyles.dividedFormContainer}>
        <TextField
          label="Cidade"
          defaultValue={submittionObject?.city}
          inputRef={cityRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={
            foundedCepAddress !== null && cityRef?.current?.value !== ""
          }
          size="small"
        />

        <TextField
          label="Estado"
          defaultValue={submittionObject?.state}
          inputRef={stateRef}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={
            foundedCepAddress !== null && stateRef?.current?.value !== ""
          }
          size="small"
          sx={{ width: { xs: "100%", md: "35%", lg: "35%" } }}
        />
      </Box>

      {foundedCepAddress && (
        <Box sx={{ alignSelf: "flex-end", marginTop: 3 }}>
          <CoordinateSelector
            coordinates={coordinates}
            setCoordinates={setCoordinates}
            brazilianAddress={foundedCepAddress}
          />
        </Box>
      )}

      <Box sx={{ ...sharedStyles.dividedFormContainer, marginTop: 3 }}>
        <PrimaryButton
          label="Confirmar"
          styles={sharedStyles.secondaryColor}
          onClickAction={handleSecondStepSubmit}
        />
        <SecondaryButton
          label="Voltar"
          onClickAction={() =>
            handlePreviousStep(additionStep, setAdditionStep)
          }
        />
      </Box>
    </Box>
  );

  const renderImageGallery = (
    <Box sx={sharedStyles.verticalContent}>
      <Box sx={{ ...sharedStyles.verticalContent, marginY: 4 }}>
        <Box sx={{ alignSelf: "center", maxWidth: "400px" }}>
          <AddImageInput
            setImages={setImages}
            icon={<AddPhotoAlternateIcon color="secondary" />}
          />
        </Box>

        {images.length > 0 ? (
          <ImageGalleryContainer>
            <GalleryImageList images={images} setImages={setImages} interable />
          </ImageGalleryContainer>
        ) : (
          <EmptyGallery />
        )}
      </Box>

      <Box sx={sharedStyles.dividedFormContainer}>
        <PrimaryButton
          label="Confirmar"
          styles={sharedStyles.secondaryColor}
          onClickAction={handleThirdStepSubmit}
        />

        <SecondaryButton
          label="Voltar"
          onClickAction={() =>
            handlePreviousStep(additionStep, setAdditionStep)
          }
        />
      </Box>
    </Box>
  );

  const renderWorkScaleSelector = (
    <Box sx={{ ...sharedStyles.verticalContent, gap: 6 }}>
      <WorkingScaleSelector
        timeMonday={timeMonday}
        setTimeMonday={setTimeMonday}
        timeTuesday={timeTuesday}
        setTimeTuesday={setTimeTuesday}
        timeWednesday={timeWednesday}
        setTimeWednesday={setTimeWednesday}
        timeThursday={timeThursday}
        setTimeThursday={setTimeThursday}
        timeFriday={timeFriday}
        setTimeFriday={setTimeFriday}
        timeSaturday={timeSaturday}
        setTimeSaturday={setTimeSaturday}
        timeSunday={timeSunday}
        setTimeSunday={setTimeSunday}
        possibleMondayTimes={POSSIBLE_WORKING_TIMES}
        possibleTuesdayTimes={POSSIBLE_WORKING_TIMES}
        possibleWednesdayTimes={POSSIBLE_WORKING_TIMES}
        possibleThursdayTimes={POSSIBLE_WORKING_TIMES}
        possibleFridayTimes={POSSIBLE_WORKING_TIMES}
        possibleSaturdayTimes={POSSIBLE_WORKING_TIMES}
        possibleSundayTimes={POSSIBLE_WORKING_TIMES}
      />

      <NewOne />

      <Box sx={sharedStyles.dividedFormContainer}>
        <PrimaryButton
          label="Adicionar agência"
          styles={sharedStyles.secondaryColor}
          isLoading={additionLoading}
          icon={<CheckIcon />}
          onClickAction={handleFourthStepSubmit}
        />

        <SecondaryButton
          label="Voltar"
          onClickAction={() =>
            handlePreviousStep(additionStep, setAdditionStep)
          }
          isDisabled={additionLoading}
        />
      </Box>
    </Box>
  );

  const renderAddStorePageContent = (
    <>
      <LeftMarginBackButton />

      <Typography variant="h6" fontWeight="bold">
        Adição de agência
      </Typography>

      <Stepper activeStep={additionStep} sx={{ width: "100%", marginY: 4 }}>
        {EXCURSION_OPERATION_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>
              <Typography
                sx={{ display: { xs: "none", md: "block", lg: "block" } }}
              >
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={sharedStyles.verticalContent}>
        {additionStep === 0
          ? renderStoreDetails
          : additionStep === 1
            ? renderGeographicData
            : additionStep === 2
              ? renderImageGallery
              : renderWorkScaleSelector}
      </Box>
    </>
  );

  return <ComercialDrawer content={renderAddStorePageContent} />;
};

export default AddAgency;
